import type { ParsingRule } from "../../types";

/**
 * @description Expected csv columns. We can list multiple name for a given column to ensure legacy or changes.
 * Use to check the file content.
 */
export const columns = [
  ["name", "technicalName"],
  ["activatedObservable"],
  ["observableSigmaDiagnosis"],
];

/**
 * @description Column regex to check the file content.
 */
const columnPattern = new RegExp(columns.map(names => `(${names.join("|")})`).join("|"), "g");

/**
 * @description Expected values for the activated observable column.
 */
const activatedObservablePattern = /^(0|1)$/;

/**
 * @description Check if file contains all required columns.
 */
export const hasInvalidColumns: ParsingRule = (content: string) => {
  const matches = content.match(columnPattern);
  return matches && matches?.length > 0 ? false : true;
};

/**
 * @description Check if file contains duplicated rows (based on name|technicalName column).
 */
export const hasDuplicatedRowByQuantityId: ParsingRule = (quantityIds: (string | undefined)[]) =>
  quantityIds.length > new Set(quantityIds).size;

/**
 * @description Check if the line contain more columns than expected.
 */
export const hasExtraColumns: ParsingRule = (data: string[]) => data.length > 0;

/**
 * @description Check if the name column, on a given line, contain empty value.
 */
export const hasNoValueForQuantityId: ParsingRule = (quantityId: string) => quantityId.length === 0;

/**
 * @description Check if the name column, on a given line, contain unexpected value.
 */
export const hasUnexpectedQuantityId: ParsingRule = (
  quantityId: string,
  expectedNameValues: string[],
) => (expectedNameValues.includes(quantityId) ? false : true);

/**
 * @description Check if the activated observable column, on a given line, contain unexpected value.
 */
export const hasInvalidActivatedObservableValue: ParsingRule = (activatedObservable: string) =>
  activatedObservablePattern.test(activatedObservable) ? false : true;

/**
 * @description Check if the sigma value, on a given line,
 * contain expected value depending on corresponding activatedObservable value.
 * It must be a number if activatedObservable equals 1.
 *
 * @note We place this check before sigma value ones as its a more specific case.
 */
export const hasInvalidSigmaForActivatedObservable: ParsingRule = (
  activatedObservable: string,
  observableSigmaDiagnosis: string,
) => (activatedObservable === "1" && isNaN(parseFloat(observableSigmaDiagnosis)) ? true : false);

/**
 * @description Check if the sigma value, on a given line, is a number.
 */
export const hasInvalidSigmaValue: ParsingRule = (observableSigmaDiagnosis: string) =>
  isNaN(parseFloat(observableSigmaDiagnosis)) ? true : false;

/**
 * @description Check if the sigma value, on a given line, is positive.
 */
export const hasNegativeSigma: ParsingRule = (observableSigmaDiagnosis: string) =>
  parseFloat(observableSigmaDiagnosis) > 0 ? false : true;

export const shouldIgnoreEmptyActivatedObservable: ParsingRule = (activatedObservable: string) =>
  activatedObservable.length === 0 ? true : false;

export const shouldSkipDefinedObservable: ParsingRule = (
  activatedObservable: string,
  observableSigmaDiagnosis: string,
) => (activatedObservable === "0" && observableSigmaDiagnosis.length === 0 ? true : false);
